import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Trophy, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { getStandings, createStanding, updateStanding, deleteStanding, getTeamCategories, getAllTeams, getAllCompetitions } from "@/lib/api";

const FUTSAL_GENT_LOGO = "https://customer-assets.emergentagent.com/job_kickoff-central-10/artifacts/72zh29ie_Middel%2020%40300x.png";

const initialFormData = {
  team_id: "",
  team_name: "",
  team_logo: "",
  played: 0,
  won: 0,
  drawn: 0,
  lost: 0,
  goals_for: 0,
  goals_against: 0,
  points: 0,
  position: 1,
  competition_id: "",
  competition: "",
  team_category: "A",
};

export default function AdminStandings() {
  const { category } = useParams();
  const [standings, setStandings] = useState([]);
  const [teamCategories, setTeamCategories] = useState([]);
  const [teams, setTeams] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const activeCategory = category || (teamCategories[0]?.code || "A");

  // Futsal Gent hardcoded
  const futsalGentTeam = {
    id: "futsal-gent-home",
    name: "Futsal Gent",
    city: "Gent",
    logo_url: FUTSAL_GENT_LOGO,
    category_id: null,
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, teamsRes, competitionsRes] = await Promise.all([
          getTeamCategories(),
          getAllTeams(),
          getAllCompetitions()
        ]);
        setTeamCategories(categoriesRes.data.filter(c => c.is_active));
        setTeams(teamsRes.data);
        setCompetitions(competitionsRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchData();
  }, []);

  const fetchStandings = useCallback(async () => {
    try {
      const response = await getStandings({ category: activeCategory });
      setStandings(response.data);
    } catch (error) {
      console.error("Error fetching standings:", error);
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => {
    setLoading(true);
    fetchStandings();
  }, [fetchStandings]);

  // Helper functions
  const getCategoryIdFromCode = (code) => {
    const category = teamCategories.find(c => c.code === code);
    return category ? category.id : null;
  };

  const getTeamsForActiveCategory = () => {
    const activeCategoryId = getCategoryIdFromCode(activeCategory);
    return teams.filter(t => 
      t.category_id === activeCategoryId || 
      !t.category_id
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    if (name === "team_id") {
      if (value === "futsal-gent-home") {
        setFormData(prev => ({ 
          ...prev, 
          team_id: value, 
          team_name: "Futsal Gent", 
          team_logo: FUTSAL_GENT_LOGO
        }));
      } else {
        const team = teams.find(t => t.id === value);
        if (team) {
          setFormData(prev => ({ 
            ...prev, 
            team_id: value, 
            team_name: team.name, 
            team_logo: team.logo_url 
          }));
        }
      }
    } else if (name === "competition_id") {
      const competition = competitions.find(c => c.id === value);
      if (competition) {
        setFormData(prev => ({ 
          ...prev, 
          competition_id: value, 
          competition: competition.name 
        }));
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validatie
    if (!formData.team_id || !formData.competition_id) {
      toast.error("Selecteer een team en competitie uit de dropdown");
      return;
    }
    
    setSaving(true);

    const data = {
      ...formData,
      team_category: activeCategory,
      played: parseInt(formData.played) || 0,
      won: parseInt(formData.won) || 0,
      drawn: parseInt(formData.drawn) || 0,
      lost: parseInt(formData.lost) || 0,
      goals_for: parseInt(formData.goals_for) || 0,
      goals_against: parseInt(formData.goals_against) || 0,
      points: parseInt(formData.points) || 0,
      position: parseInt(formData.position) || 1,
    };

    try {
      if (editingId) {
        await updateStanding(editingId, data);
        toast.success("Ranglijst succesvol bijgewerkt");
      } else {
        await createStanding(data);
        toast.success("Team succesvol toegevoegd aan ranglijst");
      }
      setDialogOpen(false);
      setFormData({ ...initialFormData, team_category: activeCategory });
      setEditingId(null);
      fetchStandings();
    } catch (error) {
      toast.error("Kon ranglijst niet opslaan");
      console.error("Error saving standing:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (standing) => {
    setFormData({
      team_id: standing.team_id || "",
      team_name: standing.team_name,
      team_logo: standing.team_logo || "",
      played: standing.played,
      won: standing.won,
      drawn: standing.drawn,
      lost: standing.lost,
      goals_for: standing.goals_for,
      goals_against: standing.goals_against,
      points: standing.points,
      position: standing.position,
      competition_id: standing.competition_id || "",
      competition: standing.competition_name || standing.competition || "",
      team_category: standing.team_category,
    });
    setEditingId(standing.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Weet je zeker dat je dit team uit de ranglijst wilt verwijderen?")) return;

    try {
      await deleteStanding(id);
      toast.success("Team succesvol verwijderd uit ranglijst");
      fetchStandings();
    } catch (error) {
      toast.error("Kon team niet verwijderen");
      console.error("Error deleting standing:", error);
    }
  };

  const openNewDialog = () => {
    setFormData({ ...initialFormData, team_category: activeCategory });
    setEditingId(null);
    setDialogOpen(true);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Ranglijst</h1>
        <Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          Team Toevoegen
        </Button>
      </div>

      {/* Category Tabs */}
      {teamCategories.length > 0 && (
        <div className="flex gap-2 mb-6">
          {teamCategories.map((cat) => (
            <Link
              key={cat.id}
              to={`/admin/standings/${cat.code}`}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeCategory === cat.code
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>
      )}

      {/* Standings Table */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-600">Laden...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50 border-slate-200">
                  <TableHead className="text-slate-600 w-12">#</TableHead>
                  <TableHead className="text-slate-600">Team</TableHead>
                  <TableHead className="text-slate-600 text-center">GS</TableHead>
                  <TableHead className="text-slate-600 text-center">W</TableHead>
                  <TableHead className="text-slate-600 text-center">G</TableHead>
                  <TableHead className="text-slate-600 text-center">V</TableHead>
                  <TableHead className="text-slate-600 text-center">DV</TableHead>
                  <TableHead className="text-slate-600 text-center">DT</TableHead>
                  <TableHead className="text-slate-600 text-center">DS</TableHead>
                  <TableHead className="text-slate-600 text-center font-bold">PTS</TableHead>
                  <TableHead className="text-right text-slate-600">Acties</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {standings.length > 0 ? (
                  standings.map((standing) => (
                    <TableRow key={standing.id} className="border-slate-200">
                      <TableCell className="font-bold text-slate-900">{standing.position}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {standing.team_logo ? (
                            <img src={standing.team_logo} alt={standing.team_name} className="h-6 w-6 object-contain" />
                          ) : (
                            <Trophy className="h-5 w-5 text-slate-400" />
                          )}
                          <span className="font-medium text-slate-900">{standing.team_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-slate-600">{standing.played}</TableCell>
                      <TableCell className="text-center text-slate-600">{standing.won}</TableCell>
                      <TableCell className="text-center text-slate-600">{standing.drawn}</TableCell>
                      <TableCell className="text-center text-slate-600">{standing.lost}</TableCell>
                      <TableCell className="text-center text-slate-600">{standing.goals_for}</TableCell>
                      <TableCell className="text-center text-slate-600">{standing.goals_against}</TableCell>
                      <TableCell className="text-center text-slate-600">
                        {standing.goal_difference >= 0 ? `+${standing.goal_difference}` : standing.goal_difference}
                      </TableCell>
                      <TableCell className="text-center font-bold text-slate-900">{standing.points}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(standing)} className="border-slate-200">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(standing.id)} className="border-slate-200 text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-slate-500">
                      Geen ranglijst gevonden. Voeg teams toe!
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{editingId ? "Ranglijst Bewerken" : "Team Toevoegen"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Team Selection */}
            <div className="space-y-2">
              <Label>Team *</Label>
              <Select value={formData.team_id} onValueChange={(v) => handleSelectChange("team_id", v)} required>
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="futsal-gent-home">Futsal Gent (Gent)</SelectItem>
                  {getTeamsForActiveCategory().map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name} {team.city ? `(${team.city})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Competition Selection */}
            <div className="space-y-2">
              <Label>Competitie *</Label>
              <Select value={formData.competition_id} onValueChange={(v) => handleSelectChange("competition_id", v)} required>
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer competitie" />
                </SelectTrigger>
                <SelectContent>
                  {competitions.map((comp) => (
                    <SelectItem key={comp.id} value={comp.id}>
                      {comp.name} ({comp.season})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Statistics Grid */}
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="played">Gespeeld</Label>
                <Input id="played" name="played" type="number" min="0" value={formData.played} onChange={handleChange} className="border-slate-300" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="won">Gewonnen</Label>
                <Input id="won" name="won" type="number" min="0" value={formData.won} onChange={handleChange} className="border-slate-300" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="drawn">Gelijk</Label>
                <Input id="drawn" name="drawn" type="number" min="0" value={formData.drawn} onChange={handleChange} className="border-slate-300" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lost">Verloren</Label>
                <Input id="lost" name="lost" type="number" min="0" value={formData.lost} onChange={handleChange} className="border-slate-300" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="goals_for">Doelpunten Voor</Label>
                <Input id="goals_for" name="goals_for" type="number" min="0" value={formData.goals_for} onChange={handleChange} className="border-slate-300" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="goals_against">Doelpunten Tegen</Label>
                <Input id="goals_against" name="goals_against" type="number" min="0" value={formData.goals_against} onChange={handleChange} className="border-slate-300" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="points">Punten</Label>
                <Input id="points" name="points" type="number" min="0" value={formData.points} onChange={handleChange} className="border-slate-300" />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-300">
                Annuleren
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                {saving ? "Opslaan..." : "Opslaan"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
